import { useEffect, useState } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { useAuth } from "context/AuthContext";
import {
  ProfileUpdateRequest,
  useChangeMyPassword,
  useMyProfile,
  useUpdateMyProfile,
} from "hooks/useProfile";
import ProfileEditTab from "components/profile/ProfileEditTab";
import ProfilePasswordTab from "components/profile/ProfilePasswordTab";
import { LoadingScreen } from "components/shared/LoadingScreen";
import { PageError } from "components/shared/PageError";
import { notify } from "utils/notify";
import {
  PageContainer,
  PageHeader,
  PageTitle,
  ContentContainer,
  DetailsTabView,
  UserInfoSection,
  UserHeader,
  UserName,
} from "styles/components/userDetails.styles";

function Profile() {
  const { user: authUser, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [editForm, setEditForm] = useState<ProfileUpdateRequest>({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const { data: profile, isLoading, error, refetch } = useMyProfile();
  const updateProfileMutation = useUpdateMyProfile();
  const changePasswordMutation = useChangeMyPassword();

  useEffect(() => {
    const source = profile ?? authUser;
    if (!source) return;
    setEditForm({
      firstName: source.firstName ?? "",
      lastName: source.lastName ?? "",
      email: source.email ?? "",
      phone: source.phone ?? "",
      city: source.city ?? "",
      country: source.country ?? "",
      language: source.language ?? "",
    });
  }, [profile, authUser]);

  const handleSaveEdit = async () => {
    setIsSavingEdit(true);
    try {
      const response = await updateProfileMutation.mutateAsync(editForm);
      if (response.user) {
        updateUser(response.user);
      }
      notify.success("Profile updated successfully.");
    } catch (err: any) {
      notify.error(
        err?.response?.data?.message || "Failed to update profile.",
      );
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleChangePassword = (
    currentPassword: string,
    newPassword: string,
  ) => {
    setIsChangingPassword(true);
    changePasswordMutation.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          notify.success("Password updated successfully.");
        },
        onError: (err: any) => {
          notify.error(
            err?.response?.data?.message || "Failed to change password.",
          );
        },
        onSettled: () => {
          setIsChangingPassword(false);
        },
      },
    );
  };

  if (isLoading && !authUser) return <LoadingScreen />;
  if (error && !authUser) {
    return (
      <PageError
        message="Failed to load your profile."
        onRetry={() => refetch()}
      />
    );
  }

  const display = profile ?? authUser;

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>My Profile</PageTitle>
      </PageHeader>

      <ContentContainer>
        <UserInfoSection>
          <UserHeader>
            <UserName>
              {display?.firstName} {display?.lastName}
            </UserName>
          </UserHeader>
          <p style={{ margin: 0, opacity: 0.8 }}>{display?.email}</p>
        </UserInfoSection>

        <DetailsTabView>
          <TabView
            activeIndex={activeTab}
            onTabChange={(e) => setActiveTab(e.index)}
          >
            <TabPanel header="Details" leftIcon="pi pi-user-edit mr-2">
              <ProfileEditTab
                form={editForm}
                isSaving={isSavingEdit}
                onChange={setEditForm}
                onSave={handleSaveEdit}
              />
            </TabPanel>
            <TabPanel header="Password" leftIcon="pi pi-lock mr-2">
              <ProfilePasswordTab
                isChangingPassword={isChangingPassword}
                onChangePassword={handleChangePassword}
              />
            </TabPanel>
          </TabView>
        </DetailsTabView>
      </ContentContainer>
    </PageContainer>
  );
}

export default Profile;
